import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { DEFAULT_PRICE_CURRENCY } from "shared/constants/app-constants"
import { exists } from "shared/lib/exists"
import { formatPrice } from "shared/lib/priceUtils"
import { WithClassName } from "shared/types/WithClassName"
import { PriceGroup } from "shared/types/__generated__/graphql"
import "shared/ui/data-table/styles/_data-table.scss"
import { Label } from "shared/ui/label/Label"

import { formatPriceGroupName } from "entities/projects/lib/formatPriceGroupName"
import { sortPriceGroups } from "entities/projects/lib/sortPriceGroups"

import "./styles/_price-groups.scss"

export interface PriceGroupsProps extends WithClassName {
  groups: PriceGroup[] | null | undefined
  isLoading?: boolean
  isUpdating?: boolean
  hideLabel?: boolean
  hideHeader?: boolean
}

export const PriceGroups = React.memo(
  ({
    groups,
    className,
    isLoading,
    isUpdating,
    hideLabel = false,
    hideHeader = true
  }: PriceGroupsProps) => {
    const { t } = useTranslation()
    const sortedPriceGroups = useMemo(
      () => (groups ? sortPriceGroups(groups) : null),
      [groups]
    )

    if (isLoading) return null

    return sortedPriceGroups ? (
      <div
        className={classNames("data-table price-groups", className, {
          "price-groups--updating": isUpdating
        })}
        data-testid="price-groups"
      >
        <Label
          className={classNames("price-groups__label", {
            "visually-hidden": hideLabel
          })}
        >
          {t("Price.PricePerGroup")}
        </Label>
        <div className="data-table__scroll-container">
          <table className="data-table__table">
            <caption
              className={classNames("price-groups__caption", "visually-hidden")}
            >
              {t("Price.PricePerGroup")}
            </caption>
            <thead
              className={classNames({
                "visually-hidden": hideHeader
              })}
            >
              <tr className="data-table__header">
                <th className="price-groups__name-header">
                  {t("Price.PriceGroup")}
                </th>
                <th className="price-groups__price-header">
                  {t("Price.IncludeSurcharge")}
                </th>
              </tr>
            </thead>

            <tbody className="data-table__body">
              {sortedPriceGroups.map(group => (
                <tr key={group.name} className="data-table__row">
                  <th
                    scope="row"
                    className="data-table__cell price-groups__name-cell"
                  >
                    {formatPriceGroupName(group, t("Price.NoGroup"))}
                  </th>
                  <td className="data-table__cell price-groups__price-cell">
                    {formatPrice({
                      price: exists(group.priceTotal) ? group.priceTotal : null,
                      currency: DEFAULT_PRICE_CURRENCY
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null
  }
)
